import { ProductSwitcher } from '@crow/ui';
import { setAllAppsOpen } from '@/components/sidebar/all-apps';
import { useCrowlingoAccount } from '@/components/sidebar/crowlingo-account';
import { railPanels } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { openAppHome } from '@/lib/app-home';
import { electronInvoke, isElectronDesktop } from '@/lib/electron-bridge';

/**
 * Opens another Crowlingo app. The desktop app shows it full window, in place of Crowdex (which
 * stays as it was, behind); without the desktop, or when it does not know the app, `openHere`.
 */
export function openCrowlingoApp(
  app: string,
  url: string,
  openHere: (app: string, url: string) => boolean,
): boolean {
  if (!isElectronDesktop()) return openHere(app, url);
  const fallBack = () => openHere(app, url);
  void electronInvoke<boolean>('crowlingo_app_show', { app, url }).then(
    (shown) => shown || fallBack(),
    fallBack,
  );
  return true;
}

/**
 * "Crowdex ⌄" at the head of the sidebar (the mockup, brand/crow-ecosysteme.html): the other
 * Crowlingo applications. In the desktop app, choosing one leaves Crowdex for it, in the same
 * window (the desktop shows it full window, Crowdex stays as it was behind). In a browser, one that
 * a plugin brings with a page (`service` in its manifest) opens in its tab here, as from All apps;
 * another opens in a new tab. The grid stays All apps': Crowdex's own apps are there.
 */
export function CrowlingoSwitcher() {
  const workbench = useWorkbench();
  const panels = railPanels(workbench);
  const organization = useCrowlingoAccount()?.organization?.name;
  // Without the desktop (or where it does not know the app): Crowdex's tab or the browser's.
  const openHere = (app: string, url: string) => {
    const panel = panels.find((candidate) => candidate.service === app);
    if (panel !== undefined) {
      setAllAppsOpen(false);
      openAppHome(panel.plugin);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    return true;
  };
  return (
    <ProductSwitcher
      compact
      current="crowdex"
      organization={organization}
      onOpen={(app, url) => openCrowlingoApp(app.id, url, openHere)}
    />
  );
}
